import React, {Component, ReactElement, ReactNode} from 'react'

import NatCondominiumRepo from '../../condominium/repository/NatCondominiumRepo'
import NatDisasterRepo from '../repository/NatDisasterRepo'
import EventEmitterExAdmin from '../../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {SearchCondition, SearchResult} from '../repository/NatGetDisasterCommentList'
import {SortOrder} from '../../../../components/shared/SortOrder'
import {AdminPagePropsSet} from '../../../../components/shared/hoc/createAdminPage'
import {Nullable} from '../../../../app/TypeHelpers'
import {
    AdminLoginInfoSessionStorageKey,
    NatAdminLoginInfoSessionStorageHelper
} from '../../../common/authentication/NatAdminLoginInfoSessionStorageHelper'
import {SessionStorageHelper} from '../../../../SessionStorageHelper'
import {
    ADMINISTORATOR_MODE,
    DISASTER_TOPIC_CATEGORY,
    VALID_OR_NOT,
    VALID_OR_NOT_CHECK,
    YES_OR_NOT
} from '../../../../CodeMaster'
import {COMMON_CONSTANTS} from '../../../../CommonConstants'
import {NatGetCondominium} from '../../condominium/repository/NatGetCondominium'
import {Messages} from '../../../../Messages'
import {SearchResultCountDisplayProps} from '../../../../styledComponents/adminPage/SearchResultCountDisplay'
import {formatDateTimeForUpdateDate} from '../../../../formatters/formatDate'
import PPCheckboxGroup from '../../../../components/shared/forms/PPCheckboxGroup'
import SortableTableHeader, {
    SortableTableHeaderCell
} from '../../../../styledComponents/adminPage/SortablePaginationTableHeader'
import SortablePaginationTable from '../../../../styledComponents/adminPage/SortablePaginationTable'
import SortablePaginationTableBody from '../../../../styledComponents/adminPage/SortablePaginationTableBody'
import {Box, Link, TableRow, TableCell, Typography, SortDirection} from '@mui/material'
import {
    FullWidthLeftRowFlexContainer,
    LeftRowFlexContainer,
    RightRowFlexContainer,
    RowFlexContainer
} from '../../../../styledComponents/FlexComponents'
import ListSearchFormArea from '../../../../styledComponents/adminPage/ListSearchFormArea'
import {PageMainArea} from '../../../../components/shared/hoc/withAdminPageTemplate'
import styled from 'styled-components'
import {AdminDefaultMediumButton} from '../../../../styledComponents/adminPage/Buttons'
import {HeadingAreaMessageParams} from '../../../../components/shared/pageBaseComponents/HeadingAreaMessageComponent'
import SearchPageActionArea from '../../../../styledComponents/adminPage/SearchPageActionArea'
import {KEY_ADMIN_DISASTER_TOPIC_LIST_SEARCH_CONDITION} from '../../../../app/AdminSessionUtil'
import {TopicInfo} from '../repository/NatGetDisasterTopicList'
import {HKConstants} from '../../../../HKConstants'
import AuthorityChecker from '../../../../components/AuthorityChecker'
import {DisasterModeInfo} from '../repository/NatGetDisasterModeInfo'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    condominiumRepo: NatCondominiumRepo
    disasterRepo: NatDisasterRepo
    eventEmitter: EventEmitterExAdmin
    location: Location
}

interface States {
    condominiumName: string
    disasterModeInfo: Nullable<DisasterModeInfo>
    topicInfo: TopicInfo[]
    searchResult: SearchResult
    searchCondition: SearchCondition
    searchConditionSortInfo: SortOrder[]
    noResultMessage: string
    isLoadCompleted: boolean
}

export default class NatAdminDisasterTopicListForm extends Component<Props & AdminPagePropsSet, States> {
    mode: Nullable<string>
    subSortKey: SortOrder = new SortOrder('topicId', 'asc')

    constructor(props: Props & AdminPagePropsSet) {
        super(props)
        this.mode = NatAdminLoginInfoSessionStorageHelper
            .getStringItemOrNull(AdminLoginInfoSessionStorageKey.SELECTED_MODE)

        // 検索条件の初期設定
        let defaultSearchCondition: SearchCondition = {}
        let lastSearchCondition: SearchCondition = {}

        // 前回検索条件が保持されていればデフォルト値として設定する
        const sessionStorageSearchCondition: string | null =
            SessionStorageHelper.getStringItemOrNull(KEY_ADMIN_DISASTER_TOPIC_LIST_SEARCH_CONDITION)

        if (sessionStorageSearchCondition) {
            lastSearchCondition = JSON.parse(sessionStorageSearchCondition)
        } else {
            // 前回検索条件が設定されていない場合、カテゴリーはすべてチェック状態とする
            defaultSearchCondition.category =
                DISASTER_TOPIC_CATEGORY.values().map(category => category.value).join(',')
        }

        if (lastSearchCondition.pageNum) {
            lastSearchCondition.pageNum = Number(lastSearchCondition.pageNum)
            lastSearchCondition.page = COMMON_CONSTANTS.FIRST_PAGE
        } else {
            defaultSearchCondition.pageNum = COMMON_CONSTANTS.ROWS_PER_PAGE_SETTINGS[0]
            defaultSearchCondition.page = COMMON_CONSTANTS.FIRST_PAGE
        }

        if (Object.keys(lastSearchCondition).length) {
            defaultSearchCondition = {...lastSearchCondition}
        }

        this.state = {
            condominiumName: '',
            disasterModeInfo: null,
            topicInfo: [],
            searchResult: {},
            searchCondition: defaultSearchCondition,
            searchConditionSortInfo: [
                new SortOrder('updateDatetime', 'desc')
            ],
            noResultMessage: '',
            isLoadCompleted: false
        }

        this.handleChangePage = this.handleChangePage.bind(this)
        this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this)
        this.handleSortTopicList = this.handleSortTopicList.bind(this)
        this.onClickSearchButton = this.onClickSearchButton.bind(this)
        this.onChangeCategory = this.onChangeCategory.bind(this)
        this.onChangeExcludePastTopics = this.onChangeExcludePastTopics.bind(this)
        this.onClickRegisterTopic = this.onClickRegisterTopic.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const {condominiumCd, condominiumRepo, disasterRepo, eventEmitter} = this.props

            // 全体モードの場合は物件名を表示するために物件情報を取得する
            if (this.mode === ADMINISTORATOR_MODE.ALL.value) {
                const condominiumResponse: Nullable<NatGetCondominium> = await this.props.asyncCallFunction(
                    condominiumRepo.getCondominium.bind(condominiumRepo, condominiumCd),
                    (httpErrorOrNetworkError) => {
                        eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )
                if (condominiumResponse?.details) {
                    this.setState({
                        condominiumName: condominiumResponse.details.condominiumName
                    })
                }
            }

            // 災害時モード設定を取得する
            const disasterModeSetting = await this.props.asyncCallFunction(
                disasterRepo.getDisasterMode.bind(disasterRepo, condominiumCd),
                (httpErrorOrNetworkError) => {
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                }
            )
            this.setState({
                disasterModeInfo: disasterModeSetting
            })

            // 災害時トピック検索（管理）を取得
            await this.searchDisasterTopicList(
                this.state.searchCondition,
                this.state.searchConditionSortInfo
            )
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    async searchDisasterTopicList(
        searchCondition: SearchCondition,
        searchConditionSortInfo: SortOrder[]
    ): Promise<void> {
        const {condominiumCd, disasterRepo, eventEmitter} = this.props
        await this.props.eventEmitter.showLoading()
        try {
            // 災害時トピック一覧を取得する
            const response = await this.props.asyncCallFunction(
                disasterRepo.searchDisasterTopicList.bind(
                    disasterRepo,
                    condominiumCd,
                    searchCondition, [...searchConditionSortInfo, this.subSortKey],
                ),
                (httpErrorOrNetworkError) => {
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
            if (response?.details) {
                this.setState({
                    topicInfo: response.details.topicInfo,
                    searchResult: response.details.searchResult,
                    noResultMessage:
                        response.details.searchResult.total === 0 ? Messages.IOPCC0014.toString('') : ''
                })
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    showInitialMessage(): void {
        if (!this.props.location || !this.props.location.state) {
            return
        }
        const initialMessage: HeadingAreaMessageParams = this.props.location.state.initialMessage
        this.props.navigate(this.props.location.pathname, {replace: true, state: {}})
        this.props.eventEmitter.showHeadingAreaMessage(initialMessage)
    }

    beforeUserAction(): void {
        this.props.eventEmitter.clearHeadingAreaMessage()
    }

    searchResults(): SearchResultCountDisplayProps {
        return {
            totalCount: this.state.searchResult.total ? this.state.searchResult.total : undefined,
            rowsPerPage: this.state.searchCondition.pageNum,
            page: this.state.searchCondition.page,
        }
    }

    async onClickSearchButton(): Promise<void> {
        this.beforeUserAction()
        // 検索条件を保持
        const lastSearchCondition: SearchCondition = {
            category: this.state.searchCondition.category,
            ExcludePastTopics: this.state.searchCondition.ExcludePastTopics,
            pageNum: this.state.searchCondition.pageNum,
            page: COMMON_CONSTANTS.FIRST_PAGE,
        }
        SessionStorageHelper.setItem(
            KEY_ADMIN_DISASTER_TOPIC_LIST_SEARCH_CONDITION, JSON.stringify(lastSearchCondition))

        const searchCondition = this.state.searchCondition
        searchCondition.page = COMMON_CONSTANTS.FIRST_PAGE
        await this.searchDisasterTopicList(
            searchCondition,
            this.state.searchConditionSortInfo
        )
        this.setState({searchCondition: searchCondition})
    }

    onClickRegisterTopic(): void {
        this.props.navigate(`/admin/condominiums/${this.props.condominiumCd}/disasters/topics/register`);
    }

    getSortDirection(sortKey: string): SortDirection {
        const sortOrder: SortOrder | undefined =
            this.state.searchConditionSortInfo.find(order => order.sortKey === sortKey)
        if (sortOrder === undefined) {
            return false
        }
        return sortOrder.sortDirection
    }

    async handleSortTopicList(sortKey: string): Promise<void> {
        this.beforeUserAction()

        let sortDirection: SortDirection = this.getSortDirection(sortKey)
        if (sortDirection === false || sortDirection === 'desc') {
            sortDirection = 'asc'
        } else {
            sortDirection = 'desc'
        }

        const sortOrders: SortOrder[] = [new SortOrder(sortKey, sortDirection)]
        await this.searchDisasterTopicList(
            this.state.searchCondition,
            sortOrders
        )
        this.setState({searchConditionSortInfo: sortOrders})
    }

    async handleChangePage(
        event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
        newPage: number,
    ): Promise<void> {
        this.beforeUserAction()
        const searchCondition: SearchCondition = this.state.searchCondition
        searchCondition.page = newPage
        await this.searchDisasterTopicList(
            searchCondition,
            this.state.searchConditionSortInfo
        )
        this.setState({searchCondition: searchCondition})
    }

    async handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
        this.beforeUserAction()

        // 検索条件を保持
        const lastSearchCondition: SearchCondition = {
            category: this.state.searchCondition.category,
            pageNum: Number(event.target.value),
            page: COMMON_CONSTANTS.FIRST_PAGE,
        }

        SessionStorageHelper.setItem(
            KEY_ADMIN_DISASTER_TOPIC_LIST_SEARCH_CONDITION, JSON.stringify(lastSearchCondition))

        const searchCondition: SearchCondition = this.state.searchCondition
        searchCondition.pageNum = Number(event.target.value)
        searchCondition.page = COMMON_CONSTANTS.FIRST_PAGE
        await this.searchDisasterTopicList(
            this.state.searchCondition,
            this.state.searchConditionSortInfo
        )
        this.setState({searchCondition: searchCondition})
    }

    static getDisasterTopicCategory(categoryCode: string): string {
        const category = DISASTER_TOPIC_CATEGORY.codeToEnumConstants(categoryCode)
        if (category) {
            return category.label
        } else {
            return ''
        }
    }

    judgeCheckCategory(category: string): boolean {
        if (!this.state.searchCondition || !this.state.searchCondition.category) {
            return false
        }
        const setArray: string[] = this.state.searchCondition.category.split(',')
        return setArray.indexOf(category) >= 0
    }

    onChangeCategory(label: string, checked: boolean): void {
        const checkedStatus = DISASTER_TOPIC_CATEGORY.values().filter(set => {
            return set.label === label
        })

        if (this.state.searchCondition.category) {
            const checkedStatusArray: string[] = this.state.searchCondition.category.split(',')
            if (checked) {
                checkedStatusArray.push(checkedStatus[0].value)
            } else {
                checkedStatusArray.splice(checkedStatusArray.indexOf(checkedStatus[0].value), 1)
            }
            const searchCondition = this.state.searchCondition
            searchCondition.category = checkedStatusArray.join(',')
            this.setState({searchCondition: searchCondition})
        } else {
            if (checked) {
                const searchCondition = this.state.searchCondition
                searchCondition.category = checkedStatus[0].value
                this.setState({searchCondition: searchCondition})
            }
        }
    }

    onChangeExcludePastTopics(label: string, checked: boolean): void {
        const searchConditions = this.state.searchCondition
        searchConditions.ExcludePastTopics = checked ?
            VALID_OR_NOT_CHECK.VALID.value : VALID_OR_NOT_CHECK.NOT.value
        this.setState({searchCondition: searchConditions})
    }

    renderCategoryCheckBox(): ReactNode {
        const labels: { label: string, checked: boolean }[] = []
        const categoryMaster = DISASTER_TOPIC_CATEGORY.values()

        categoryMaster.map((category) => (
            labels.push(
                {
                    label: category.label,
                    checked: this.judgeCheckCategory(category.value),
                }
            )
        ))

        return (
            <StyledPPCheckboxGroup
                row
                data={labels}
                onChangeChecked={this.onChangeCategory}
            />
        )
    }

    renderTopicListInfo(topicInfo: TopicInfo[]): Nullable<ReactElement> {
        if (topicInfo.length === 0) {
            return null
        }

        const headers: SortableTableHeaderCell[] = [];
        headers.push(
            {id: 'title', label: 'タイトル', align: 'left'},
            {id: 'category', label: 'カテゴリ', align: 'left'},
            {id: 'pastTopic', label: '過去のトピック', align: 'center'},
            {id: 'modifierName', label: '更新者', align: 'left'},
            {
                id: 'updateDatetime',
                label: '更新日時',
                align: 'left',
                sortable: true,
                direction: this.getSortDirection('updateDatetime'),
            },
            {
                id: 'latestPostDatetime',
                label: '最新コメント日時',
                align: 'left',
                sortable: true,
                direction: this.getSortDirection('latestPostDatetime'),
            }
        );
        return (
            <SortablePaginationTable
                paginationProps={{
                    count: this.state.searchResult.total ? this.state.searchResult.total : 0,
                    rowsPerPage:
                        this.state.searchCondition.pageNum ? this.state.searchCondition.pageNum : 0,
                    page: this.state.searchCondition.page ? this.state.searchCondition.page : 0,
                    onPageChange: this.handleChangePage,
                    onRowsPerPageChange: this.handleChangeRowsPerPage,
                }}
            >
                <SortableTableHeader
                    noWrap
                    headers={headers}
                    onSortOrderChange={this.handleSortTopicList}
                />
                <SortablePaginationTableBody>
                    {topicInfo.map(
                        recode => this.renderTopicListInfoRecord(recode),
                    )}
                </SortablePaginationTableBody>
            </SortablePaginationTable>
        )
    }

    renderSearchArea(): ReactNode {
        const {condominiumName} = this.state
        return (
            <>
                <Box pl={'8px'}>
                    {this.mode === ADMINISTORATOR_MODE.ALL.value &&
                        <SearchRowContainer>
                            <SearchColumnContainer>物件名</SearchColumnContainer>
                            <SearchInputContainer>
                                {condominiumName}
                            </SearchInputContainer>
                        </SearchRowContainer>
                    }
                    <SearchRowContainer>
                        <SearchColumnContainer>カテゴリ</SearchColumnContainer>
                        <SearchInputContainer>
                            {this.renderCategoryCheckBox()}
                        </SearchInputContainer>
                    </SearchRowContainer>
                    <RowFlexContainer>
                        <SearchColumnContainer>過去のトピック</SearchColumnContainer>
                        <SearchInputContainer>
                            <StyledPPCheckboxGroup
                                row
                                data={[{
                                    label: '非表示にする',
                                    checked: this.state.searchCondition.ExcludePastTopics
                                        === VALID_OR_NOT_CHECK.VALID.value,
                                }]}
                                onChangeChecked={this.onChangeExcludePastTopics}
                            />
                        </SearchInputContainer>
                        <Box flex={1} ml={'12px'}>
                            <RightRowFlexContainer>
                                <SearchButton onClick={this.onClickSearchButton}>
                                    検索する
                                </SearchButton>
                            </RightRowFlexContainer>
                        </Box>
                    </RowFlexContainer>
                </Box>
            </>
        )
    }

    renderTopicListInfoRecord(recode: TopicInfo): ReactElement {
        const topicDetailUrl: string
            = `/admin/condominiums/${this.props.condominiumCd}/disasters/topics/${recode.topicId}`
        return (
            <TableRow key={recode.topicId}>
                <TableCell style={{width: '445px'}}>
                    <LinkOverflowWrapper>
                        <Link href={topicDetailUrl}>
                            <LinkOverflowEllipsis>
                                {recode.title}
                            </LinkOverflowEllipsis>
                        </Link>
                    </LinkOverflowWrapper>
                </TableCell>
                <TableCell style={{width: '167px'}}>
                    {NatAdminDisasterTopicListForm.getDisasterTopicCategory(recode.category)}
                </TableCell>
                <TableCell style={{width: '140px'}} align={'center'}>
                    {recode.isPast === YES_OR_NOT.YES.value ? '✓' : ''}
                </TableCell>
                <TableCellWordBreakAll style={{width: '196px'}}>
                    {recode.modifierRoleId ? recode.modifierFamilyName + ' ' + recode.modifierFirstName : '標準トピック'}
                </TableCellWordBreakAll>
                <TableCell style={{width: '167px'}}>
                    {formatDateTimeForUpdateDate(recode.updateDatetime)}
                </TableCell>
                <TableCell>
                    {recode.latestPostDatetime ? formatDateTimeForUpdateDate(recode.latestPostDatetime) : '未投稿'}
                </TableCell>
            </TableRow>
        )
    }

    render(): Nullable<ReactElement> {
        const {disasterModeInfo, topicInfo, noResultMessage} = this.state

        if (!this.state.isLoadCompleted) {
            return null
        }
        return (
            <>
                <Box height={'10px'}/>
                <ListSearchFormArea
                    searchResult={this.searchResults()}
                    onEnterKey={this.onClickSearchButton}>
                    {this.renderSearchArea()}
                </ListSearchFormArea>
                <SearchPageActionArea>
                    {disasterModeInfo?.mode === VALID_OR_NOT.VALID.value
                        ? <AuthorityChecker
                            permittedAuthorities={HKConstants.CMN.AUTH.REGISTER_TOPIC_DISASTER_INFORMATION_SHARING}>
                            <AdminDefaultMediumButton onClick={this.onClickRegisterTopic}>
                                新規
                            </AdminDefaultMediumButton>
                        </AuthorityChecker>
                        : <></>
                    }
                </SearchPageActionArea>
                {topicInfo.length === 0
                    ? <Box mt={'16px'} ml={'10px'} mb={'14px'}>
                        <Typography variant={'body2'}>
                            {noResultMessage}
                        </Typography>
                    </Box>
                    : <PageMainArea>
                        {this.renderTopicListInfo(topicInfo)}
                    </PageMainArea>
                }
            </>
        )
    }
}

const SearchRowContainer = styled(FullWidthLeftRowFlexContainer)`
    padding: 8px 0;
`

const SearchInputContainer = styled.div`
    margin-left: 16px;
`

const SearchColumnContainer = styled(LeftRowFlexContainer)`
    width: 186px;
    flex-shrink: 0;
`

const SearchButton = styled(AdminDefaultMediumButton)`
    &.MuiButtonBase-root {
        width: 204px;
        height: 42px;
    }
`

const StyledPPCheckboxGroup = styled(PPCheckboxGroup)`
    & .MuiTypography-root {
        font-size: 14px;
    }
`

const TableCellWordBreakAll = styled(TableCell)`
    &.MuiTableCell-root {
        word-break: break-all;
    }
`
const LinkOverflowWrapper = styled.div`
    width: 420px;
`

const LinkOverflowEllipsis = styled.div`
    display: inline-block;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

